import logoDarkDefault from "@public/logo.svg";
import logoLightDefault from "@public/logo_on_light.svg";
import { Info, Loader2 } from "lucide-react";
import { useTheme } from "next-themes";
import { useId } from "react";
import { ThemeToggle } from "@/components/theme-toggle";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n/use-t";
import { FieldProgressBar } from "@/pages/clients/detail/flows/instance/flow-progress-bar";
import { T } from "./public-flow-header.i18n";

type PublicFlowHeaderProps = {
  flowName: string;
  clientName: string;
  tenantLogoLightUrl?: string | null;
  tenantLogoDarkUrl?: string | null;
  saving: boolean;
  mounted: boolean;
  language: string;
  isCompleting?: boolean;
  flowDescription?: string | null;
  /**
   * Submit (public §3.8). Submit lives in the header for both single-goal and
   * multi-goal flows — no inline content-body button. `showSubmit` is false in
   * the completion view and once the flow is completed; `submitDisabled`
   * carries the suitability-blocker / in-flight gate. The in-flight spinner
   * reuses `isCompleting`.
   */
  showSubmit?: boolean;
  onSubmit?: () => void;
  submitDisabled?: boolean;
  /**
   * QL-239 — the flow is in preview: Submit stays visible but disabled, with a
   * short notice so the client is not left guessing. Server-derived.
   */
  submitBlockedByPreview?: boolean;
  /**
   * Engine-driven progress (Follow-up C P1). Reuses the advisor `FieldProgressBar`.
   * Shown while the flow is still being filled (`showProgress`); hidden in the
   * completion view and once submitted. Replaces the old page-index block (§3.11).
   */
  showProgress?: boolean;
  completionPercent?: number;
  filledRequired?: number;
  totalRequired?: number;
};

/**
 * Public flow header — logo row (saving indicator, theme toggle), then the flow
 * name + description with Submit beside it, then the progress bar on a line of
 * its own.
 *
 * QL-682 — no status chip (ticket ask; a locked instance is flagged by the
 * "form completed" alert in the body), and Submit sits next to the title so the
 * progress bar gets the full width on narrow screens.
 */
export function PublicFlowHeader({
  flowName,
  clientName,
  tenantLogoLightUrl,
  tenantLogoDarkUrl,
  saving,
  mounted,
  language,
  isCompleting,
  flowDescription,
  showSubmit,
  onSubmit,
  submitDisabled,
  submitBlockedByPreview,
  showProgress,
  completionPercent = 0,
  filledRequired = 0,
  totalRequired = 0,
}: PublicFlowHeaderProps) {
  const { resolvedTheme } = useTheme();
  const t = useT(T);

  // Get tenant logo based on theme
  const isDark = mounted && resolvedTheme === "dark";
  const previewNoticeId = useId();
  const showPreviewNotice = !!(showSubmit && onSubmit && submitBlockedByPreview);
  const tenantLogo = isDark ? tenantLogoDarkUrl : tenantLogoLightUrl;
  const defaultLogo = isDark ? logoDarkDefault : logoLightDefault;
  const logoSrc = tenantLogo || defaultLogo;

  return (
    /* Width cap matches the content container in public-flow-content-inner. */
    <div className="max-w-7xl mx-auto px-4 py-4">
      {/* Tenant Logo */}
      <div className="flex items-center justify-between mb-4">
        {tenantLogo ? (
          // biome-ignore lint/a11y/useAltText: logo image
          <img src={logoSrc} className="h-8 max-w-[120px] object-contain" />
        ) : (
          // biome-ignore lint/a11y/useAltText: logo image
          <img src={logoSrc} className="h-8 object-contain" />
        )}
        <div className="flex items-center gap-2">
          {saving && !isCompleting ? (
            <div className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {t.saving}
            </div>
          ) : null}
          <ThemeToggle />
        </div>
      </div>
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="min-w-0">
          <h1 className="text-xl font-bold">{flowName}</h1>
          {flowDescription && (
            <p className="text-sm text-muted-foreground mt-1">{flowDescription}</p>
          )}
          <p className="text-sm text-muted-foreground">
            {t.welcome} {clientName}
          </p>
        </div>
        {showSubmit && onSubmit && (
          <Button
            size="sm"
            className="shrink-0"
            onClick={onSubmit}
            disabled={submitDisabled || submitBlockedByPreview}
            loading={isCompleting}
            aria-describedby={showPreviewNotice ? previewNoticeId : undefined}
          >
            {t.submit}
          </Button>
        )}
      </div>
      {/* QL-239 — full width under the title row so it reads on narrow screens. */}
      {showPreviewNotice && (
        <Alert variant="info" className="mb-2">
          <Info />
          <AlertDescription id={previewNoticeId}>{t.previewNotice}</AlertDescription>
        </Alert>
      )}
      {showProgress && (
        <FieldProgressBar
          filledCount={filledRequired}
          totalCount={totalRequired}
          percent={completionPercent}
          language={language}
          className="mt-3"
          barClassName="w-full"
        />
      )}
    </div>
  );
}
